import { forwardRef, useCallback } from "react"

// --- Lib ---
import { parseShortcutKeys } from "@/tiptap/lib/tiptap-utils"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import {
  AI_ASK_SHORTCUT_KEY,
  useAiAsk,
} from "@/tiptap/ui/ai-ask-button"

import { Button } from "@/tiptap/ui-primitive/button"
import { Badge } from "@/tiptap/ui-primitive/badge"

/**
 * Badge component displaying the AI shortcut key
 */
export function AskAiShortcutBadge({
  shortcutKeys = AI_ASK_SHORTCUT_KEY
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for triggering AI ask functionality on selected content in a Tiptap editor.
 */
export const AiAskButton = forwardRef(function AiAskButton(
  {
    editor: providedEditor,
    text,
    hideWhenUnavailable = false,
    onAiAsked,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) {
  const { editor } = useTiptapEditor(providedEditor)
  const { isVisible, canAiAsk, handleAiAsk, label, shortcutKeys, Icon } =
    useAiAsk({
      editor,
      hideWhenUnavailable,
      onAiAsked,
    })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleAiAsk()
  }, [handleAiAsk, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      variant="ghost"
      disabled={!canAiAsk}
      data-disabled={!canAiAsk}
      role="button"
      tabIndex={-1}
      aria-label={label}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <Icon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
          {showShortcut && <AskAiShortcutBadge shortcutKeys={shortcutKeys} />}
        </>
      )}
    </Button>
  );
})
